import { z } from 'zod';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { useEvalReportPersistSetup } from '@/shared/hooks/report/use-eval-report-persist-setup';
import { ClassType, ReportTab } from '@/shared/types/report-phase2/constant';

import { reportTchQueries } from '@/entities/phase2-report';
import { usePhase2ReportStore } from '@/entities/phase2-report/model/report.store';

import { EvalReportAnswer } from '@/widgets/report/phase2/report-contents/answer-section';
import { ReportHeader } from '@/widgets/report/phase2/report-header';
import { EvalReportSummary } from '@/widgets/report/phase2/report-summary';
import { EvalReportAiReview } from '@/widgets/report/phase2/report-contents/ai-review-section';
import useTypeCodeGubun from '@/widgets/report/phase2/report-contents/answer-section/hooks/use-type-code-gubun';
import { useLoginStore, usePersistStore } from '@/shared/lib/store';
import { Modal } from '@/shared/ui';
import { useModalStore } from '@/shared/lib/store/model/modal.store';

type TabType = 'answer' | 'ai-total';

const REQUIRED = [
    'ct',
    'evalid',
    'rtab',
    'direct',
    'classSeq',
    'type',
    'from',
    'userMid',
    'curriBookCd',
] as const;

const searchSchema = z.object({
    ct: z.union([z.literal(ClassType.WeClass), z.literal(ClassType.Student)]),
    evalid: z.preprocess((v) => Number(v), z.number()),
    rtab: z.enum([ReportTab.Answer, ReportTab.AiTotal]).optional().default(ReportTab.Answer),
    from: z.preprocess((v) => {
        if (v === 'undefined' || v === undefined || v === null) return undefined;
        return String(v);
    }, z.string().optional()), // vdt에서 접속시 사용
    type: z.string().optional(),
    publishId: z.number().optional(),
    direct: z.union([z.literal('y'), z.literal('n')]).optional(),
    curriBookCd: z.number().optional(),
    userMid: z.string().optional(),
    classSeq: z.number().optional(),
});

export const Route = createFileRoute('/report/eval/$gubun/_eval/_report/')({
    validateSearch: (search) => searchSchema.parse(search),
    component: RouteComponent,
});

function RouteComponent() {
    const { gubun } = Route.useParams();
    const search = useSearch({ strict: false });
    const { sToken } = usePersistStore();
    const {
        ct,
        evalid,
        from,
        rtab: currentTab,
        type,
        direct,
        userMid,
        curriBookCd,
        classSeq,
    } = Route.useSearch();
    const { stntId, title, selectedPersistStntId, setStntId, stntSaveSearch, setStntSaveSearch } =
        usePhase2ReportStore();
    const { isTeacher, isStudent } = useTypeCodeGubun();
    const navigate = Route.useNavigate();
    const { loginInfo } = useLoginStore();
    const isDirect = direct === 'y';
    const isStntReport = gubun === 's' && isDirect;
    const [first, setFirst] = useState(true);
    const { showModal, closeModal } = useModalStore();

    const onClose = () => {
        closeModal('moal-alert');
        window.location.replace(loginUrl);
    };
    const loginUrl = isTeacher
        ? import.meta.env.VITE_LOGIN_URL
        : import.meta.env.VITE_VIVACLASS_LOGIN;

    const directErrorModal = () => {
        showModal({
            isCustom: true,
            id: 'moal-alert',
            initContents: () => (
                <Modal
                    open={true}
                    overlay={false}
                    onClose={() => onClose()}
                    title="알림"
                    description={
                        <>
                            AI 스마트 평가는
                            <br />
                            교사 인증을 완료한 정회원 대상 서비스입니다.
                            <br />
                            비바샘 사이트에서 교사 인증 후 사용해 주세요!
                        </>
                    }
                    onConfirm={onClose}
                    onConfirmText="확인"
                ></Modal>
            ),
        });
    };

    useEffect(() => {
        if (loginInfo.loginYn !== 'L' && gubun === 's' && direct === 'y') {
            if (isStntReport && loginInfo) {
                if (loginInfo.loginYn === 'Y') {
                    if (loginInfo.teacherYn === 'Y') {
                        navigate({
                            to: '/report/eval/$gubun',
                            params: { gubun: 't' },
                            search: {
                                ct: 'weclass',
                                evalid: evalid,
                                rtab: currentTab,
                                direct: direct,
                                classSeq: classSeq,
                                type: type,
                                from: from,
                            },
                        });
                    } else if (
                        loginInfo.studentYn === 'Y' &&
                        !!userMid &&
                        !!curriBookCd &&
                        !!classSeq
                    ) {
                        setStntId(userMid + '-' + classSeq + '-' + curriBookCd);
                    }
                } else {
                    directErrorModal();
                }
            } else {
                directErrorModal();
            }
        }
    }, [loginInfo, isStntReport, gubun, direct, curriBookCd, userMid, classSeq]);

    useEffect(() => {
        if (loginInfo?.studentYn === 'Y' && direct === 'y' && gubun === 's') {
            const allPresent = REQUIRED.every(
                (k) => search[k] !== undefined && search[k] !== null && String(search[k]) !== ''
            );

            if (!stntSaveSearch && allPresent) {
                const baseline = Object.fromEntries(REQUIRED.map((k) => [k, search[k]]));
                setStntSaveSearch(JSON.stringify(baseline));
                return;
            }
            if (!stntSaveSearch) return;
            const baseline = JSON.parse(stntSaveSearch) as Record<string, unknown>;
            const missing = REQUIRED.some(
                (k) => baseline[k] != null && (search[k] == null || String(search[k]) === '')
            );

            if (missing) {
                navigate({ to: '.', search: baseline as any, replace: true });
            }
        }
        return () => {
            setStntSaveSearch(null);
        };
    }, [search, loginInfo, first, stntSaveSearch, gubun, direct]);

    // 평가리포트 persist 셋업 훅
    useEvalReportPersistSetup();

    const isWeClass = ct === ClassType.WeClass;
    const isAnswer = 'answer';
    const isAiReview = 'ai-total';

    // 우리반 서머리
    reportTchQueries.weclass.useEvalReportSummary(
        {
            evlId: evalid, // 평가 아이디
        },
        (isWeClass || ct === ClassType.Student) && !!sToken
    );

    // 학생 서머리
    reportTchQueries.students.useStntEvalReportSummary(
        gubun === 's'
            ? {
                  evlId: evalid,
                  stntId: stntId ?? '',
                  isStntOnly: 'Y',
              }
            : {
                  evlId: evalid,
                  stntId: stntId ?? '',
              },
        !isWeClass && !!sToken
    );

    // AI 총평 정보
    const { data: aiReviewData } = reportTchQueries.review.useReportEvalReviewInfo(
        {
            userId: stntId ?? selectedPersistStntId ?? '',
            evlId: evalid,
        },
        ct === ClassType.Student && !!sToken
    );

    // ai총평 탭 노출 조건
    const showAiReviewTab = useMemo(() => {
        if (isTeacher) return true;
        if (isStudent && aiReviewData?.stdtPrntRlsAt === 'Y' && aiReviewData?.genrvw !== '')
            return true;
        return false;
    }, [isTeacher, isStudent, aiReviewData]);

    // 탭 리스트 동적 생성
    const tabList = useMemo(() => {
        const baseTabs = [{ label: '답안 보기', value: isAnswer }];
        if (showAiReviewTab) {
            baseTabs.push({ label: 'AI 총평', value: isAiReview });
        }
        return baseTabs;
    }, [showAiReviewTab]);

    const contentsTabHandler = (rtab: TabType) => {
        navigate({
            search: (prev) => ({ ...prev, rtab }),
            replace: true,
        });
    };

    return (
        <div className="contwrap">
            <div className="contInner report-content">
                {/* TODO: 고도화, 타이틀, 타입 추가 */}
                <ReportHeader title={title ?? ''} publishType={type ?? 'url'} from={from} />
                <EvalReportSummary />
                <div className="tab-menu tab-type1 tab-type8 tab-tit2">
                    <ul className="tab-list">
                        {tabList.map((tabInfo) => (
                            <TabButton
                                key={tabInfo.value}
                                label={tabInfo.label}
                                isActive={currentTab === tabInfo.value}
                                onClick={() => contentsTabHandler(tabInfo.value as TabType)}
                            />
                        ))}
                    </ul>
                </div>
                <div className="con-mt3">
                    <div className="result">
                        <div className="report_study">
                            <div className="table-con">
                                <div className="cont">
                                    {currentTab === isAnswer && <EvalReportAnswer />}
                                    {currentTab === isAiReview && <EvalReportAiReview />}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface TabButtonProps {
    label: string;
    isActive: boolean;
    onClick: () => void;
}

function TabButton({ label, isActive, onClick }: TabButtonProps) {
    return (
        <li className={`tab-item ${isActive ? 'isCurrent' : ''}`}>
            <button type="button" className="tab-btn" onClick={onClick}>
                {label}
            </button>
        </li>
    );
}
